import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { calculateAgeOnDate } from 'src/util/util';
import { toLocalDate } from 'src/util/partialDate';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export default function InlineRecordCapture({
  record,
  patientDob,
  maxAge,
  dateField,
  ageField,
  dateFormat,
  dateLabel,
  ageLabel,
  onSaveCapture,
  onSkip
}) {
  const t = useAutoTranslation();

  const hasDob = Boolean(patientDob);
  // Always default to age input — clinicians typically have the age at the
  // time of recording, not the exact date. Users can flip to date mode via
  // the "Enter date instead" affordance below.
  const [inputMode, setInputMode] = useState('age');
  const [dateValue, setDateValue] = useState(null);
  const [ageValue, setAgeValue] = useState('');
  const [ageError, setAgeError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Inline validation: an age greater than the person's current age (or age
  // at death, computed by the parent) is impossible. Surface immediately so
  // the user can correct without re-opening — previously this was silently
  // caught by the diagnoses save handler and only flashed in a snackbar
  // after the inline had already collapsed.
  const validateAge = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) return t('Age must be a positive number');
    if (typeof maxAge === 'number' && Number.isFinite(maxAge) && num > maxAge) {
      return t("Age cannot exceed the person's age ({{maxAge}}).", { maxAge });
    }
    return '';
  };
  // Mirror the controlled values in refs so handleSubmit can read the latest
  // input even when the browser delivers the trailing onChange and the Enter
  // keyDown in the same task tick — observed in Chrome but not Safari, where
  // React 18 batching otherwise leaves the closure one render behind.
  const ageValueRef = useRef('');
  const dateValueRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus?.();
    }, 100);
    return () => clearTimeout(timer);
  }, [inputMode]);

  const handleSubmit = () => {
    if (saving) return;
    const liveDate = dateValueRef.current;
    const liveAge = ageValueRef.current;
    if (inputMode === 'date' && !liveDate) return onSkip();
    if (inputMode === 'age' && !liveAge) return onSkip();

    // Block submission on invalid age — keep the inline open so the user
    // can correct it. The error is also displayed via the TextField's
    // helperText below.
    if (inputMode === 'age') {
      const err = validateAge(liveAge);
      if (err) {
        setAgeError(err);
        return;
      }
    }

    setSaving(true);
    const update = { ...record };
    if (inputMode === 'date') {
      update[dateField] = liveDate.format('YYYY-MM-DD');
      if (hasDob) {
        // The age on the calendar day just written, read as the calendar date
        // it is (toLocalDate); blank before the birth. calculateAge counted
        // elapsed time from a UTC-midnight birth date: a year short on the
        // birthday at a positive offset, and positive before the birth.
        update[ageField] = String(
          calculateAgeOnDate(patientDob, update[dateField], toLocalDate)
        );
      }
    } else {
      update[ageField] = String(liveAge);
    }
    onSkip();
    onSaveCapture(update);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onSkip();
    }
  };

  const switchMode = () => {
    setDateValue(null);
    setAgeValue('');
    setAgeError('');
    dateValueRef.current = null;
    ageValueRef.current = '';
    setInputMode((prev) => (prev === 'date' ? 'age' : 'date'));
  };

  return (
    <Box
      sx={{
        px: 1.5,
        pb: 1.5,
        pt: 1,
        mb: 0.5,
        bgcolor: '#fafafa',
        border: '1px solid #ccc',
        borderTop: 'none',
        borderRadius: '0 0 5px 5px'
      }}
    >
      {inputMode === 'date' ? (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={dateLabel}
            format={dateFormat || 'DD/MM/YYYY'}
            value={dayjs.isDayjs(dateValue) ? dateValue : null}
            onChange={(val) => {
              dateValueRef.current = val;
              setDateValue(val);
            }}
            disableFuture
            minDate={patientDob ? dayjs(patientDob) : undefined}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                onKeyDown: handleKeyDown,
                inputRef
              }
            }}
          />
        </LocalizationProvider>
      ) : (
        <TextField
          label={ageLabel}
          type="number"
          size="small"
          fullWidth
          value={ageValue}
          onChange={(e) => {
            const v = e.target.value;
            ageValueRef.current = v;
            setAgeValue(v);
            setAgeError(validateAge(v));
          }}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          error={!!ageError}
          helperText={ageError || undefined}
          slotProps={{
            htmlInput: { min: 0, max: maxAge }
          }}
        />
      )}
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={switchMode}
        >
          {inputMode === 'date'
            ? t('Enter age instead')
            : t('Enter date instead')}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            cursor: 'pointer',
            color: 'text.secondary',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={onSkip}
        >
          {t('Skip')}
        </Typography>
      </Box>
    </Box>
  );
}
